import { useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { 
  Settings, 
  HelpCircle, 
  RefreshCw, 
  ArrowLeft, 
  Sword, 
  Backpack,
  Heart,
  Vibrate,
  X,
  MoreVertical
} from "lucide-react";

export function GameFloatingMenu({
  onSettings,
  onHelp,
  onRestart,
  onLevelMap,
  onInventory,
  onToggleHaptic,
  hapticEnabled,
  hapticSupported,
  hp,
  inventoryCount = 0,
  swordTier,
  swordMode,
  swordUsesRemaining,
  maxSwordUses,
  onSwordToggle,
  onOpenSwordSelection,
  hasGodSword,
  godSwordMode,
  godSwordUsesRemaining,
  onGodSwordToggle,
  disabled = false
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      {/* Backdrop when menu is open */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/40 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
      
      {/* Floating Menu Button */}
      <div className="fixed bottom-6 right-4 z-50">
        {/* Menu Items - Show when open */}
        {isOpen && (
          <div className="absolute bottom-16 right-0 flex flex-col gap-2 animate-in slide-in-from-bottom-2 duration-200">
            {/* Settings */}
            <MenuIconButton
              icon={<Settings className="w-5 h-5" />}
              label="Settings"
              onClick={() => { onSettings?.(); setIsOpen(false); }}
            />
            
            {/* Help */}
            <MenuIconButton
              icon={<HelpCircle className="w-5 h-5" />}
              label="Help"
              onClick={() => { onHelp?.(); setIsOpen(false); }}
            />
            
            {/* Inventory */}
            <MenuIconButton
              icon={<Backpack className="w-5 h-5" />}
              label={`Items (${inventoryCount})`}
              onClick={() => { onInventory?.(); setIsOpen(false); }}
              badge={inventoryCount > 0 ? inventoryCount : null}
            />
            
            {/* Restart */}
            <MenuIconButton
              icon={<RefreshCw className="w-5 h-5" />}
              label="Restart"
              onClick={() => { onRestart?.(); setIsOpen(false); }}
              disabled={hp < 20}
              variant="danger"
            />
            
            {/* Level Map */}
            <MenuIconButton
              icon={<ArrowLeft className="w-5 h-5" />}
              label="Level Map"
              onClick={() => { onLevelMap?.(); setIsOpen(false); }}
            />
            
            {/* Haptic Toggle */}
            {hapticSupported && (
              <MenuIconButton
                icon={<Vibrate className="w-5 h-5" />}
                label={hapticEnabled ? "Vibration On" : "Vibration Off"}
                onClick={() => { onToggleHaptic?.(); }}
                active={hapticEnabled}
              />
            )}
          </div>
        )}
        
        {/* Main Menu Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          disabled={disabled}
          className={cn(
            "w-14 h-14 rounded-full flex items-center justify-center",
            "bg-gradient-to-br from-slate-700 to-slate-800",
            "border-2 border-white/20",
            "shadow-lg shadow-black/30",
            "transition-all duration-200",
            isOpen ? "rotate-90 bg-red-600" : "hover:scale-105",
            disabled && "opacity-50"
          )}
          data-testid="game-menu-btn"
        >
          {isOpen ? (
            <X className="w-6 h-6 text-white" />
          ) : (
            <MoreVertical className="w-6 h-6 text-white" />
          )}
        </button>
      </div>
      
      {/* Sword Button - Left side when available - Opens selection */}
      {swordTier && (
        <div className="fixed bottom-6 left-4 z-50">
          <button
            onClick={swordMode ? onSwordToggle : onOpenSwordSelection}
            disabled={disabled}
            className={cn(
              "w-14 h-14 rounded-full flex items-center justify-center",
              "border-2 shadow-lg transition-all duration-200",
              swordMode 
                ? "bg-gradient-to-br from-amber-500 to-orange-600 border-amber-300 animate-pulse" 
                : swordUsesRemaining > 0
                  ? "bg-gradient-to-br from-amber-100 to-yellow-100 border-amber-400 hover:scale-105"
                  : "bg-slate-700 border-slate-600 opacity-50",
              disabled && "opacity-50"
            )}
            data-testid="sword-btn"
          >
            <Sword className={cn(
              "w-6 h-6",
              swordMode ? "text-white" : swordUsesRemaining > 0 ? "text-amber-700" : "text-slate-400"
            )} />
            {swordUsesRemaining > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                {swordUsesRemaining}
              </span>
            )}
          </button>
          {/* Label below button */}
          {!swordMode && (
            <p className="text-center text-xs text-slate-400 mt-1 font-medium">
              {swordUsesRemaining > 0 ? 'Swords' : 'Used'}
            </p>
          )}
        </div>
      )}
      
      {/* God Sword Button - Above regular sword when available */}
      {hasGodSword && godSwordUsesRemaining > 0 && (
        <div className="fixed bottom-24 left-4 z-50">
          <button
            onClick={onGodSwordToggle}
            disabled={disabled}
            className={cn(
              "w-12 h-12 rounded-full flex items-center justify-center",
              "border-2 shadow-lg transition-all duration-200",
              godSwordMode 
                ? "bg-gradient-to-br from-yellow-400 to-amber-500 border-yellow-300 animate-pulse" 
                : "bg-gradient-to-br from-yellow-50 to-amber-50 border-yellow-400 hover:scale-105",
              disabled && "opacity-50"
            )}
            data-testid="god-sword-btn"
          >
            <span className="text-xl">⚔️</span>
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-yellow-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
              {godSwordUsesRemaining}
            </span>
          </button>
        </div>
      )}
    </>
  );
}

// Individual menu item button
function MenuIconButton({ icon, label, onClick, disabled, badge, variant, active }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex items-center gap-3 px-4 py-3 rounded-xl",
        "bg-slate-800/95 backdrop-blur-sm",
        "border border-white/10",
        "shadow-lg",
        "transition-all duration-200",
        "min-w-[160px]",
        variant === "danger" 
          ? "hover:bg-red-600/80 text-red-300 hover:text-white" 
          : active 
            ? "bg-orange-600/80 text-white"
            : "hover:bg-slate-700 text-white",
        disabled && "opacity-50 cursor-not-allowed"
      )}
    >
      <span className={cn(
        variant === "danger" ? "text-red-400" : active ? "text-orange-300" : "text-slate-400"
      )}>
        {icon}
      </span>
      <span className="font-medium text-sm flex-1 text-left">{label}</span>
      {badge && (
        <span className="px-2 py-0.5 bg-orange-500 text-white text-xs font-bold rounded-full">
          {badge}
        </span>
      )}
    </button>
  );
}

// Hint Button - Always visible
export function GameHintButton({ onClick, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "fixed bottom-6 left-1/2 -translate-x-1/2 z-50",
        "px-6 py-3 rounded-full",
        "bg-gradient-to-r from-yellow-400 to-amber-500",
        "border-2 border-yellow-300",
        "shadow-lg shadow-amber-500/30",
        "transition-all duration-200",
        "hover:scale-105 hover:shadow-amber-500/50",
        "flex items-center gap-2",
        disabled && "opacity-50 cursor-not-allowed"
      )}
      data-testid="hint-btn"
    >
      <span className="text-xl">💡</span>
      <span className="font-bold text-amber-900">Hint</span>
    </button>
  );
}

// Mobile-Optimized Game Header
export function GameHeader({
  levelName,
  levelSubtitle,
  goal,
  moves,
  movesLeft,
  onBack,
  score = 0,
  targetEmoji,
  targetCount,
  fruitsCollected = 0,
  ghostScore = null,
}) {
  const isLowMoves = movesLeft <= 5;
  
  return (
    <div className="fixed top-0 left-0 right-0 z-40 safe-area-top">
      {/* Main Header Bar */}
      <div className="bg-slate-900/95 backdrop-blur-xl border-b border-white/10">
        <div className="flex items-center justify-between px-3 py-2 max-w-lg mx-auto">
          {/* Back Button */}
          <button
            onClick={onBack}
            className={cn(
              "w-11 h-11 rounded-xl flex items-center justify-center",
              "bg-slate-800/80 active:bg-slate-700",
              "border border-white/10",
              "transition-colors"
            )}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          
          {/* Level Name - Center */}
          <div className="flex-1 mx-3 text-center">
            <p className="text-white font-bold text-base leading-tight">{levelName}</p>
            <p className="text-orange-400 text-xs leading-tight truncate">{goal}</p>
          </div>
          
          {/* Moves Counter - Right */}
          <div className={cn(
            "min-w-[56px] h-11 px-3 rounded-xl flex flex-col items-center justify-center",
            "border transition-all",
            isLowMoves 
              ? "bg-red-500/20 border-red-500/50 animate-pulse" 
              : "bg-slate-800/80 border-white/10"
          )}>
            <p className={cn(
              "text-xl font-black leading-none",
              isLowMoves ? "text-red-400" : "text-white"
            )}>
              {movesLeft}
            </p>
            <p className={cn(
              "text-[9px] uppercase font-medium leading-none mt-0.5",
              isLowMoves ? "text-red-300" : "text-slate-400"
            )}>
              Moves
            </p>
          </div>
        </div>
        
        {/* Secondary Info Bar - Score & Progress */}
        <div className="flex items-center justify-center gap-4 px-3 pb-2">
          {/* Score */}
          <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-800/60">
            <span className="text-orange-400 font-bold text-sm">{score.toLocaleString()}</span>
            <span className="text-slate-500 text-xs">pts</span>
          </div>
          
          {/* Fruit Progress (if target exists) */}
          {targetEmoji && targetCount && (
            <div className={cn(
              "flex items-center gap-1.5 px-3 py-1 rounded-full",
              fruitsCollected >= targetCount 
                ? "bg-green-500/20 border border-green-500/30" 
                : "bg-slate-800/60"
            )}>
              <span className="text-lg leading-none">{targetEmoji}</span>
              <span className={cn(
                "font-bold text-sm",
                fruitsCollected >= targetCount ? "text-green-400" : "text-white"
              )}>
                {fruitsCollected}/{targetCount}
              </span>
            </div>
          )}

          {/* Ghost Score - Friend's best */}
          {ghostScore && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-slate-800/40 border border-indigo-500/20" data-testid="ghost-score">
              <span className="text-[10px] text-indigo-300">{ghostScore.name}</span>
              <span className={`text-xs font-bold ${score > ghostScore.score ? 'text-green-400' : 'text-indigo-400'}`}>
                {ghostScore.score.toLocaleString()}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
